import { CalendarOutlined, HomeOutlined, LinkOutlined } from '@ant-design/icons'
import { SBFormItemAddon } from '@components/SBFormItemAddon'
import { SBAsyncSelect } from '@supplement-bacon/react-async-select'
import {
  Button,
  Card,
  Flex,
  Form,
  Input,
  Select,
  Space,
  Typography,
} from 'antd'
import { CSSProperties, useEffect } from 'react'
import { useTenant } from '../../../../modules/tenant/contexts/tenant/TenantContext'
import { Entity } from '../../../../utils/Entities'
import { urlRule } from '../../../../utils/rules'
import { ContentWrapper } from '../components/ContentWrapper'
import { StepProps } from '../types'
const { Title, Text } = Typography

type Props = StepProps & {
  viewOnly?: boolean
}

export const Action = ({ onPrev, onNext, viewOnly }: Props) => {
  const { tabs } = useTenant().tenant.app

  const form = Form.useFormInstance()
  const type = Form.useWatch(['action', 'type'])
  const venue = Form.useWatch(['action', 'venue'])
  const event = Form.useWatch(['action', 'event'])
  const app = Form.useWatch(['action', 'app'])
  const external_link = Form.useWatch(['action', 'external_link'])

  useEffect(() => {
    if (venue) {
      form.setFieldValue(['action', 'type'], 'venue')
    } else if (type === 'venue') {
      form.setFieldValue(['action', 'type'], undefined)
    }
  }, [venue])

  useEffect(() => {
    if (event) {
      form.setFieldValue(['action', 'type'], 'event')
    } else if (type === 'event') {
      form.setFieldValue(['action', 'type'], undefined)
    }
  }, [event])

  useEffect(() => {
    if (app) {
      form.setFieldValue(['action', 'type'], 'app')
    } else if (type === 'app') {
      form.setFieldValue(['action', 'type'], undefined)
    }
  }, [app])

  useEffect(() => {
    if (external_link && external_link !== '') {
      form.setFieldValue(['action', 'type'], 'external_link')
    } else if (type === 'external_link') {
      form.setFieldValue(['action', 'type'], undefined)
    }
  }, [external_link])

  const styleForCard = (typeOfCard: string): CSSProperties => ({
    borderColor: type === typeOfCard ? '#8e978a' : undefined,
    borderWidth: type === typeOfCard ? 2 : 1,
    opacity: type !== typeOfCard && type !== undefined ? 0.4 : 1,
    cursor:
      typeOfCard === 'none' &&
      !viewOnly &&
      (type === typeOfCard || type === undefined)
        ? 'pointer'
        : undefined,
  })

  return (
    <>
      <ContentWrapper>
        <Title className="m-0" level={5}>
          Specify the push action
        </Title>
        <Card className="w-full" style={styleForCard('venue')}>
          <Flex gap={20}>
            <Text style={{ fontSize: 20 }}>
              <HomeOutlined />
            </Text>
            <Space orientation="vertical" size={2} className="w-full">
              <Title level={5} className="m-0">
                Open venue page
              </Title>
              <Text>
                To open a venue page in the app upon clicking the notification
              </Text>
              <Form.Item name={['action', 'venue']} className="w-full">
                <SBAsyncSelect
                  allowClear
                  entity={Entity.venue}
                  searchFieldName="name"
                  disabled={
                    viewOnly || (type !== 'venue' && type !== undefined)
                  }
                />
              </Form.Item>
            </Space>
          </Flex>
        </Card>
        <Card className="w-full" style={styleForCard('event')}>
          <Flex gap={20}>
            <Text style={{ fontSize: 20 }}>
              <CalendarOutlined />
            </Text>
            <Space orientation="vertical" size={2} className="w-full">
              <Title level={5} className="m-0">
                Open event page
              </Title>
              <Text>
                To open a event page in the app upon clicking the notification
              </Text>
              <Form.Item name={['action', 'event']} className="w-full">
                <SBAsyncSelect
                  allowClear
                  entity={Entity.event}
                  searchFieldName="title"
                  disabled={
                    viewOnly || (type !== 'event' && type !== undefined)
                  }
                />
              </Form.Item>
            </Space>
          </Flex>
        </Card>
        <Card className="w-full" style={styleForCard('external_link')}>
          <Flex gap={20}>
            <Text style={{ fontSize: 20 }}>
              <LinkOutlined />
            </Text>
            <Space orientation="vertical" className="w-full" size={2}>
              <Title level={5} className="m-0">
                Open external Link
              </Title>
              <Text>
                To redirect the user to an external web link upon clicking the
                notification
              </Text>
              <SBFormItemAddon
                formItemProps={{
                  name: ['action', 'external_link'],
                  rules: [urlRule()],
                }}
                addonBefore={'Link'}
              >
                <Input
                  allowClear
                  style={{ width: '100%' }}
                  disabled={
                    viewOnly || (type !== 'external_link' && type !== undefined)
                  }
                />
              </SBFormItemAddon>
            </Space>
          </Flex>
        </Card>
        <Card className="w-full" style={styleForCard('app')}>
          <Flex gap={20}>
            <Text style={{ fontSize: 20 }}>
              <LinkOutlined />
            </Text>
            <Space orientation="vertical" size={2} className="w-full">
              <Title level={5} className="m-0">
                Open app
              </Title>
              <Text>
                To redirect the user to a specific tab in the app upon clicking
                the notification
              </Text>
              <Form.Item name={['action', 'app']} className="w-full">
                <Select
                  options={tabs.map((tab) => ({
                    value: tab.value,
                    label: tab.name,
                  }))}
                  disabled={viewOnly || (type !== 'app' && type !== undefined)}
                  allowClear
                />
              </Form.Item>
            </Space>
          </Flex>
        </Card>
        <Form.Item name={['action', 'type']} hidden>
          <Input hidden />
        </Form.Item>
      </ContentWrapper>
      <Flex justify="flex-end" gap={8}>
        <Button type="default" onClick={onPrev} disabled={false}>
          Prev
        </Button>
        <Button
          type="primary"
          onClick={onNext}
          disabled={type === null || type === undefined}
        >
          Next
        </Button>
      </Flex>
    </>
  )
}
